import { styled } from 'styled-components';
import { Tag, PureIconButton } from '@/ui';
import { COLORS, pickThemeFontSize, pickThemeFontStyles } from '@/theme';

export const StyledPreviewCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 22rem;
  background-color: ${COLORS.WHITE};
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 1.25rem;
  box-shadow: 0 0.75rem 2rem rgba(16, 24, 40, 0.16);
  overflow: hidden;
  font-family: var(--font-figtree);
  max-width: 25.25rem;
  min-height: 28rem;

  .ant-input,
  .ant-tag {
    font-family: inherit;
  }
`;

export const StyledPreviewHeader = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background-color: ${({ $color }) => $color};
  transition: background-color 0.15s ease;
`;

export const StyledHeaderAvatar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2.375rem;
  height: 2.375rem;
  font-size: ${pickThemeFontSize('22')};
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.22);
  color: ${COLORS.WHITE};
`;

export const StyledHeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
`;

export const StyledHeaderName = styled.div`
  ${pickThemeFontStyles('15', '18', 'BOLD')};
  color: ${COLORS.WHITE};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StyledHeaderStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  ${pickThemeFontStyles('12', '16')};
  color: rgba(255, 255, 255, 0.85);
`;

export const StyledStatusDot = styled.span`
  width: 0.4375rem;
  height: 0.4375rem;
  border-radius: 50%;
  background-color: ${COLORS.ONLINE};
`;

export const StyledHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
`;

export const StyledHeaderIconButton = styled(PureIconButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border-radius: 0.625rem;
  background-color: rgba(255, 255, 255, 0.18);
  color: ${COLORS.WHITE};

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

export const StyledPreviewBody = styled.div`
  display: flex;
  padding: 1.25rem 1rem;
  flex-grow: 1;
  background-color: ${COLORS.WHITE};
`;

export const StyledMessageAvatar = styled.div<{ $color: string }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  font-size: ${pickThemeFontSize('18')};
  margin-right: 0.75rem;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  transition: background-color 0.15s ease;
  color: ${COLORS.WHITE};
`;

export const StyledMessageAvatarBadge = styled.div<{ $color: string }>`
  position: absolute;
  right: -0.125rem;
  bottom: -0.125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background-color: ${COLORS.WHITE};
  box-shadow: rgba(0, 0, 0, 0.28) 0px 1px 3px;
  color: ${({ $color }) => $color};

  svg {
    width: 0.625rem;
    height: 0.625rem;
  }
`;

export const StyledMessageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
`;

export const StyledMessageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
`;

export const StyledSenderName = styled.span`
  ${pickThemeFontStyles('13', '15', 'BOLD')};
  color: ${COLORS.TEXT_PRIMARY};
`;

export const StyledAiBadge = styled(Tag)<{ $color: string }>`
  &.ant-tag {
    ${pickThemeFontStyles('9', '11')};
    color: ${({ $color }) => $color};
    background-color: ${({ $color }) => `${$color}1F`};
    margin: 0;
    display: inline-flex;
    align-items: center;
    gap: 0.1875rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    padding: 0.125rem 0.375rem;
    border-radius: 0.375rem;

    svg {
      width: 0.625rem;
      height: 0.625rem;
    }
  }
`;

export const StyledPreviewMessage = styled.div`
  ${pickThemeFontStyles('14', '24')};
  white-space: pre-wrap;
  text-wrap-style: pretty;
  color: ${COLORS.TEXT_PRIMARY};
`;

export const StyledPreviewFooter = styled.div`
  padding: 0 1rem 1rem;
  background-color: ${COLORS.WHITE};
`;

export const StyledInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3125rem 0.3125rem 0.3125rem 0.75rem;
  background-color: ${COLORS.COLOR_SEARCH};
  border: 1px solid ${COLORS.COLOR_BORDER_SECONDARY};
  border-radius: 0.875rem;
  height: 3.25rem;

  .ant-input {
    padding: 0;
    background-color: transparent;
    ${pickThemeFontStyles('15', '18')};

    &::placeholder {
      color: ${COLORS.PLACEHOLDER};
    }
  }
`;

export const StyledSendButton = styled(PureIconButton)<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border-radius: 0.6875rem;
  background-color: ${({ $color }) => $color};
  color: ${COLORS.WHITE};
  transition: background-color 0.15s ease;

  svg {
    width: 0.9375rem;
    height: 0.9375rem;
    fill: transparent;
  }
`;

export const StyledDisclaimer = styled.div`
  margin-top: 0.5rem;
  ${pickThemeFontStyles('11', '13')};
  color: ${COLORS.AI_CHAT_HINT};
  text-align: center;
`;
